import { SlidersHorizontal, X } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import type { PreviewCreator } from "@/types/mission-assign"

/**
 * MissionAssignHandpickControl — search + checkbox roster for the
 * hand-pick mode (UX Spec §3 + §5).
 *
 * The roster is `availableMemberships`, which the BE serialises as the
 * brand's active members (capped at 200) on every `#show`. This means
 * cold-start (no `crew_membership_ids[]` in URL) renders a fully
 * usable picker — selection then drives URL reload via `onToggleId`
 * which round-trips through `useAssignmentUrl`.
 *
 * **Search:** local filter. The displayed value is debounced (default
 * 250 ms — UX Spec §3 contract) so the filter doesn't thrash on every
 * keystroke. Tests can pass `searchDebounceMs={0}` to disable.
 *
 * **Select-all on page:** indeterminate-state checkbox header that
 * selects every visible (post-filter) row, or clears them. Tracks
 * page-scoped state, not roster-wide.
 *
 * **Filter Sheet:** entrypoint button opens a Sheet exposing the OKS-25
 * five-filter taxonomy (location, follower range, tags, join-date
 * range, name search). The sheet is a stub for V1 — when OKS-25's
 * `crew-filter-rail` lands we swap the body for the real component.
 * The entrypoint + dimensions exist now so the surface is operable
 * and discoverable.
 */
interface MissionAssignHandpickControlProps {
  /** Brand-active memberships available to pick. */
  availableMemberships: PreviewCreator[]
  /** Currently-selected ids (URL-driven). */
  selectedIds: number[]
  /** Toggle a single id. */
  onToggleId: (id: number) => void
  /** Replace the whole selection — used by select-all-on-page. */
  onSetSelection?: (ids: number[]) => void
  /** Clear all selections (selection chip X / "Clear" CTA). */
  onClearSelection: () => void
  /** Search debounce window. Defaults to 250 ms; tests pass 0. */
  searchDebounceMs?: number
  testId?: string
}

export function MissionAssignHandpickControl({
  availableMemberships,
  selectedIds,
  onToggleId,
  onSetSelection,
  onClearSelection,
  searchDebounceMs = 250,
  testId = "mission-assign-handpick-control",
}: MissionAssignHandpickControlProps) {
  const { t } = useTranslation()
  const [search, setSearch] = useState("")
  const debouncedSearch = useDebouncedValue(search, searchDebounceMs)

  const filtered = useMemo(() => {
    const needle = debouncedSearch.trim().toLowerCase()
    if (needle === "") return availableMemberships
    return availableMemberships.filter((m) => {
      const handle = (m.handle ?? "").toLowerCase()
      const name = (m.name ?? "").toLowerCase()
      return handle.includes(needle) || name.includes(needle)
    })
  }, [availableMemberships, debouncedSearch])

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])

  const visibleIds = useMemo(() => filtered.map((m) => m.id), [filtered])
  const visibleSelectedCount = visibleIds.reduce(
    (acc, id) => (selectedSet.has(id) ? acc + 1 : acc),
    0,
  )
  const allVisibleSelected =
    visibleIds.length > 0 && visibleSelectedCount === visibleIds.length
  const someVisibleSelected = visibleSelectedCount > 0 && !allVisibleSelected

  function handleSelectAllOnPage(next: boolean) {
    if (!onSetSelection) {
      // Fallback: toggle missing ids one at a time. Lets the control
      // stay operable for callers that haven't wired onSetSelection.
      visibleIds.forEach((id) => {
        const has = selectedSet.has(id)
        if (next && !has) onToggleId(id)
        if (!next && has) onToggleId(id)
      })
      return
    }
    if (next) {
      const merged = new Set(selectedIds)
      visibleIds.forEach((id) => merged.add(id))
      onSetSelection(Array.from(merged))
    } else {
      const visibleSet = new Set(visibleIds)
      onSetSelection(selectedIds.filter((id) => !visibleSet.has(id)))
    }
  }

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Input
          type="search"
          placeholder={t("missions.assign.handpick.search_placeholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="mission-assign-handpick-search"
          className="flex-1"
        />
        <HandpickFilterSheet />
      </div>

      {selectedIds.length > 0 && (
        <div
          className="flex flex-wrap items-center gap-2"
          data-testid="mission-assign-handpick-selected-chips"
        >
          <p
            role="status"
            aria-live="polite"
            className="text-muted-foreground text-xs"
          >
            {t("missions.assign.handpick.selected_count", {
              count: selectedIds.length,
            })}
          </p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClearSelection}
            data-testid="mission-assign-handpick-clear"
          >
            <X className="size-3" aria-hidden="true" />
            {t("missions.assign.handpick.clear_selection")}
          </Button>
        </div>
      )}

      {filtered.length === 0 ? (
        <p
          className="text-muted-foreground py-4 text-center text-sm"
          data-testid="mission-assign-handpick-empty"
        >
          {t("missions.assign.handpick.empty_results")}
        </p>
      ) : (
        <div
          className="rounded border"
          data-testid="mission-assign-handpick-list-wrapper"
        >
          <div className="bg-muted/30 flex items-center gap-3 border-b px-3 py-2">
            <Checkbox
              id="mission-assign-handpick-select-all"
              checked={allVisibleSelected}
              indeterminate={!allVisibleSelected && someVisibleSelected}
              onCheckedChange={(next) => handleSelectAllOnPage(next === true)}
              aria-label={t("missions.assign.handpick.select_all_on_page")}
              data-testid="mission-assign-handpick-select-all"
            />
            <label
              htmlFor="mission-assign-handpick-select-all"
              className="text-muted-foreground cursor-pointer text-xs"
            >
              {t("missions.assign.handpick.select_all_on_page")}
            </label>
          </div>
          <ul
            className="max-h-96 overflow-y-auto"
            data-testid="mission-assign-handpick-list"
          >
            {filtered.map((m) => {
              const checked = selectedSet.has(m.id)
              return (
                <li
                  key={m.id}
                  data-testid={`mission-assign-handpick-row-${m.id}`}
                  className="flex items-center gap-3 border-b px-3 py-2 last:border-b-0"
                >
                  <Checkbox
                    id={`mission-assign-handpick-checkbox-${m.id}`}
                    checked={checked}
                    onCheckedChange={() => onToggleId(m.id)}
                    aria-label={m.name ?? m.handle ?? `#${m.id}`}
                  />
                  <label
                    htmlFor={`mission-assign-handpick-checkbox-${m.id}`}
                    className="flex flex-1 cursor-pointer flex-col"
                  >
                    <span className="text-foreground text-sm">
                      {m.name ?? m.handle ?? `#${m.id}`}
                    </span>
                    {m.handle && (
                      <span className="text-muted-foreground text-xs">
                        @{m.handle}
                      </span>
                    )}
                  </label>
                  {m.tags.length > 0 && (
                    <Badge variant="outline" className="text-xs">
                      {m.tags[0]}
                    </Badge>
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}

// Stub filter sheet — exposes the OKS-25 five filter dimensions
// behind a button so the entrypoint exists and is operable. When
// OKS-25's `crew-filter-rail` lands we swap the body for the real
// component; until then the sheet shows labelled (disabled) inputs
// and a helper line explaining the gap.
function HandpickFilterSheet() {
  const { t } = useTranslation()

  return (
    <Sheet>
      <SheetTrigger
        render={
          <Button
            type="button"
            variant="outline"
            data-testid="mission-assign-handpick-filter-button"
          >
            <SlidersHorizontal className="mr-1 size-4" aria-hidden="true" />
            {t("missions.assign.handpick.filter_button")}
          </Button>
        }
      />
      <SheetContent
        side="right"
        className="w-full sm:max-w-md"
        data-testid="mission-assign-handpick-filter-sheet"
      >
        <SheetHeader>
          <SheetTitle>
            {t("missions.assign.handpick.filter_sheet_title")}
          </SheetTitle>
          <SheetDescription>
            {t("missions.assign.handpick.filter_sheet_description")}
          </SheetDescription>
        </SheetHeader>
        <div className="flex flex-col gap-4 px-4 py-2">
          <FilterStub
            label={t("missions.assign.handpick.filter_label_location")}
          />
          <FilterStub
            label={t("missions.assign.handpick.filter_label_followers")}
          />
          <FilterStub label={t("missions.assign.handpick.filter_label_tags")} />
          <FilterStub
            label={t("missions.assign.handpick.filter_label_join_date")}
          />
          <FilterStub label={t("missions.assign.handpick.filter_label_name")} />
          <p className="text-muted-foreground text-xs">
            {t("missions.assign.handpick.filter_unavailable_helper")}
          </p>
        </div>
        <SheetFooter>
          <SheetClose
            render={
              <Button type="button" variant="outline">
                {t("missions.assign.handpick.filter_clear")}
              </Button>
            }
          />
          <SheetClose
            render={
              <Button type="button">
                {t("missions.assign.handpick.filter_apply")}
              </Button>
            }
          />
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

function FilterStub({ label }: { label: string }) {
  const id = `handpick-filter-${label.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id} className="text-xs">
        {label}
      </Label>
      <Input id={id} disabled />
    </div>
  )
}

// Tiny local debounce — no dependency on a shared hooks file. Returns
// `value` after `delay` ms of no further updates. `delay === 0` is a
// pass-through (synchronous return), used by Vitest specs that don't
// want to fake timers.
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    if (delay <= 0) return
    const handle = window.setTimeout(() => setDebounced(value), delay)
    return () => window.clearTimeout(handle)
  }, [value, delay])

  return delay <= 0 ? value : debounced
}
